import DateInput from "@/components/FormControl/Date/DateInput";
import Select from "@/components/FormControl/Select/Select";
import PaginatedSelect from "@/components/FormControl/Select/PaginatedSelect";
import { useAppDispatch } from "@/store/hooks";
import type { RootState } from "@/store/rootReducer";
import { getAssetItemNames, getTypeNames } from "@/store/slices/assetManagementSlice";
import { getCityList, getLocalityList, getStateList } from "@/store/slices/customerSlice";
// @ts-ignore - @mui/icons-material is installed without bundled type declarations
import { Badge as BadgeIcon, Business as BusinessIcon, CalendarMonth as CalendarMonthIcon, Close as CloseIcon, Description as DescriptionIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, FilterAlt as FilterAltIcon, FilterList as FilterListIcon, LocationOn as LocationOnIcon, Person as PersonIcon, School as SchoolIcon, Store as StoreIcon, Tune as TuneIcon } from "@mui/icons-material";
import { Box, Button, IconButton, Popover, TextField } from "@mui/material";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode, type ElementType } from "react";
import { useSelector } from "react-redux";
import { type appliedFilter, type list, type showFilters, EMPTY_LOCATION, INITIAL_FILTERS } from "./FilterInterface";
import { DATE_FILTER_CONFIG, FILTER_CONFIG, GEOFENCE_FILTER_CONFIG, LOCATION_FIELDS, SPECIAL_SELECT_CONFIG, TEXT_FILTER_CONFIG } from "./FilterConfig";

type FilterKey = Exclude<keyof showFilters, "location">;

interface FilterProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: any) => void;
  defaultDateType?: string;
  filterName?: string;
  width?: string;
  appliedFilter: appliedFilter;
  showFilters?: showFilters;
  list?: list;
  /** Paginated customer/lead fetch for filter dropdown (e.g. Deal & Pipeline). Other modules omit this. */
  onFetchCustomerPage?: (
    page: number,
    search: string
  ) => Promise<{ items: any[]; total: number }>;
  defaultDateLabel?: string;
}

const PRIMARY_BLUE = "#5867dd";

const FILTER_SECTION_ICONS: Record<string, ElementType> = {
  date: CalendarMonthIcon,
  year: SchoolIcon,
  location: LocationOnIcon,
  status: PersonIcon,
  statusType: PersonIcon,
  designation: BadgeIcon,
  department: BusinessIcon,
  branch: StoreIcon,
  role: DescriptionIcon,
  manager: PersonIcon,
  user: PersonIcon,
  createdBy: PersonIcon,
  assignedTo: PersonIcon,
};

const normalizeValue = (val: any): any => {
  if (Array.isArray(val)) return val.map((v) => normalizeValue(v)).sort();
  if (dayjs.isDayjs(val)) return val.format("YYYY-MM-DD");

  if (val && typeof val === "object") {
    const obj: any = {};
    Object.keys(val).forEach((key) => {
      obj[key] = normalizeValue(val[key]);
    });
    return obj;
  }

  return val ?? null;
};

const buildComparableFilter = (data: any) => ({
  ...INITIAL_FILTERS,
  ...data,
  fromDate: data?.fromDate
    ? dayjs(data.fromDate).format("YYYY-MM-DD")
    : null,
  toDate: data?.toDate
    ? dayjs(data.toDate).format("YYYY-MM-DD")
    : null,
  fromYear: data?.fromYear
    ? dayjs(data.fromYear).format("YYYY")
    : null,
  toYear: data?.toYear
    ? dayjs(data.toYear).format("YYYY")
    : null,
});

const FILTER_GRID_MAP = {
  customer: 4,

  secondarySalesOrder: 3,
  users: 3,
  assetLocation: 3,
  expenseApproval: 3,
  myExpenseClaim: 3,
  opportunity: 3,

  salesOrder: 2,
  order: 2,
  callDetails: 2,
  dealsAndPipelines: 2,
  callView: 2,
  customerView: 2,
  dealsAndPipelinesFollowUps: 2,
  dealsLostReason: 2,
  leadAgainstAccount: 2,
  opportunityAgainstLead: 2,
  salePerformanceViewDetails: 2,
  tagOpportunityView: 2,
  taskDetailsView: 2,
  rosterPlan: 2,
  teamClaim: 2,
  asset: 2,
  assetAllocation: 2,
  userSeparation: 2,
  visitCalendar: 2,
  devicechangeLogs: 2,
  expenseApprover: 2,
  locationCoverage: 2,
  leaveManagement: 2,
  teamList: 2,
  attendanceList: 2,
  GeofenceManagement: 2,
  holiday: 2,
  timeline: 2,
};

const GRID_CLASSES: Record<number, string> = {
  1: "grid gap-4 grid-cols-1",
  2: "grid gap-4 grid-cols-2",
  3: "grid gap-4 grid-cols-3",
  4: "grid gap-4 grid-cols-4",
};

const getFilterGridClassName = (filterName?: string) => {
  const cols = FILTER_GRID_MAP[filterName as keyof typeof FILTER_GRID_MAP] || 1;
  return GRID_CLASSES[cols];
};

const getLocationGridTemplateColumns = (filterName?: string): string => {
  if (filterName === "customer") return "repeat(4, 1fr)";
  if (filterName === "order") return "repeat(3, 1fr)";
  return "repeat(2, 1fr)";
};

const getTextGridTemplateColumns = (filterName?: string): string => {
  if (filterName === "secondarySalesOrder" || filterName === "order") return "grid gap-4 grid-cols-2";
  return "grid gap-4 grid-cols-1";
};

const sectionLabelClassName = "mb-1 font-medium text-[#6c757d] text-[12px] font-['poppins']";

// Outlined "boxed" input styling shared across all filter fields
const BOX_INPUT_SX = {
  // --- MUI Select / TextField (outlined) ---
  // Applies whether the styled element IS the outlined root (Select)
  // or CONTAINS it so both look identical.
  "&.MuiOutlinedInput-root, & .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    height: 40,
  },
  "& .MuiOutlinedInput-input": {
    padding: "8.5px 14px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e3eb",
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline, &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#c4c9d6",
  },
  // Keep disabled fields (dependent selects like State/City) the same border color.
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline, & .Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e3eb",
  },

  // --- MUI X Date Pickers (v8 uses its own PickersOutlinedInput classes) ---
  "& .MuiPickersOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    height: 40,
    alignItems: "center",
  },
  // Vertically center the placeholder/value within the 40px box.
  "& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersOutlinedInput-input": {
    paddingTop: 0,
    paddingBottom: 0,
  },
  "& .MuiPickersOutlinedInput-notchedOutline": {
    borderColor: "#e0e3eb",
    borderRadius: "8px",
  },
  "&:hover .MuiPickersOutlinedInput-notchedOutline": {
    borderColor: "#c4c9d6",
  },

  // Keep the calendar icon from making date boxes taller than selects.
  "& .MuiInputAdornment-root .MuiIconButton-root": {
    padding: "4px",
  },
};

const boxDateSlotProps = (extra: Record<string, any> = {}) => ({
  textField: {
    variant: "outlined" as const,
    size: "small" as const,
    fullWidth: true,
    sx: BOX_INPUT_SX,
    ...extra,
  },
});

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <p className={sectionLabelClassName}>{children}</p>
);

const buildPlaceholder = (label?: string) =>
  label ? `Select ${String(label).toLowerCase()}` : "Select";

const FilterSectionHeader = ({
  title,
  icon: Icon = TuneIcon,
  expanded = true,
  onToggle,
}: {
  title: string;
  icon?: ElementType;
  expanded?: boolean | undefined;
  onToggle?: () => void;
}) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2 min-w-0">
      <Icon sx={{ fontSize: 18, color: PRIMARY_BLUE, flexShrink: 0 }} />
      <span className="font-semibold text-[14px] text-[#212529] truncate">{title}</span>
    </div>
    {onToggle && (
      <IconButton size="small" onClick={onToggle} sx={{ color: "#74788d" }}>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    )}
  </div>
);

const GenericSelectFilter = memo(({
  config,
  filters,
  updateFilter,
  options,
  filterName,
  customOnChange,
  onFetchCustomerPage,
  paginatedSelectResetKey,
  locationClassName,
  hideLabel,
  showFilter,
  hideTitle,
}: any) => {
  const title = config.getTitle?.(filterName) || config.title;
  const Icon = FILTER_SECTION_ICONS[config.showFilterKey] || TuneIcon;
  const label = config.getLabel?.(filterName) || config.label;
  const field = {
    value: filters?.[config.filterKey],
    onChange: (v: any) =>
      customOnChange
        ? customOnChange(v)
        : updateFilter(config.filterKey, v),
  };

  const isMulti = showFilter?.mode === "multi";

  return (
    <div className={`${locationClassName ? locationClassName : "px-1 py-2"}`}>
      {!hideLabel && !hideTitle && (
        <FilterSectionHeader title={title} icon={Icon} />
      )}

      {config.label && <FieldLabel>{config.label}</FieldLabel>}

      {config.usePaginatedSelect ? (
        <PaginatedSelect
          key={paginatedSelectResetKey}
          resetKey={paginatedSelectResetKey}
          field={field}
          options={options}
          onFetchPage={onFetchCustomerPage}
          idKey={config.idKey}
          nameKey={config.nameKey}
          variant="outlined"
          size="small"
          hideLabel
          placeholder={buildPlaceholder(config.label)}
          sx={BOX_INPUT_SX}
        />
      ) : (
        <Select
          label={label}
          field={field}
          options={options}
          idKey={config.idKey}
          nameKey={config.nameKey}
          disabled={config.disabled?.(filters)}
          isMulti={isMulti}
          returnObject
          enableSearch={config.enableSearch ?? true}
          variant="outlined"
          size="small"
          hideLabel
          placeholder={buildPlaceholder(config.label)}
          sx={BOX_INPUT_SX}
        />
      )}
    </div>
  );
});

const GenericDateFilter = memo(({
  title,
  label,
  filterKey,
  filters,
  updateFilter,
}: any) => (
  <div className="px-1 py-2">
    <FilterSectionHeader title={title} icon={CalendarMonthIcon} />
    <FieldLabel>{label}</FieldLabel>
    <DateInput
      field={{
        value: filters?.[filterKey],
        onChange: (v: any) =>
          updateFilter(filterKey, v),
      }}
      format="DD MMM, YYYY"
      maxDate={dayjs()}
      slotProps={boxDateSlotProps({ placeholder: "Select date" })}
    />
  </div>
));

const GenericTextFilter = memo(({
  title,
  placeholder,
  filterKey,
  type = "text",
  filters,
  updateFilter,
}: any) => (
  <div className="px-1 py-2">
    <FilterSectionHeader title={title} icon={TuneIcon} />
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      type={type}
      placeholder={placeholder}
      value={filters?.[filterKey]}
      onChange={(e) =>
        updateFilter(filterKey, e.target.value)
      }
      sx={BOX_INPUT_SX}
    />
  </div>
));

const FilterComponent: React.FC<FilterProps> = ({
  anchorEl,
  onClose,
  onApply,
  filterName,
  width,
  defaultDateType,
  appliedFilter,
  showFilters,
  list,
  onFetchCustomerPage,
  defaultDateLabel,
}) => {
  const open = Boolean(anchorEl);

  const dateTypeOptions = useMemo(
    () => [
      {
        label: "Create Date",
        value: filterName === "customer" || filterName === "mytask" ? "CreatedDate" : "CreateDate",
      },
      { label: "Modified Date", value: "ModifiedDate" },
      ...((filterName !== "customer" && filterName !== "users" && filterName !== "userAuditLog" && filterName !== "mytask") ? [{ label: "Close Date", value: "CloseDate" }] : []),
    ],
    [filterName]
  );

  const dispatch = useAppDispatch();
  const { stateList, cityList, localityList } = useSelector((state: RootState) => state.customer);
  const { typeIdsList, assetItemIdsList } = useSelector((state: RootState) => state.asset);

  const [filters, setFilters] = useState<any>(INITIAL_FILTERS);
  const [dateError, setDateError] = useState<string>("");
  const [yearError, setYearError] = useState<string>("");
  const [paginatedSelectResetKey, setPaginatedSelectResetKey] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    date: true,
    year: true,
    location: true,
  });

  const isLeaveModule = filterName === "leaveManagement";

  const defaultDateTypeOption = useMemo(() => {
    return (
      dateTypeOptions.find(
        (item) => item.value === defaultDateType
      ) || null
    );
  }, [dateTypeOptions, defaultDateType]);

  const updateFilter = useCallback((key: keyof appliedFilter, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  }, []);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const specialOptionsMap = useMemo(() => ({
    typeIdsList,
    assetItemIdsList,
  }), [typeIdsList, assetItemIdsList]);

  const locationOptionsMap = useMemo(
    () => ({
      countryList: list?.countryList || [],
      stateList,
      cityList,
      localityList,
    }),
    [list?.countryList, stateList, cityList, localityList]
  );

  const hasVisibleLocationFilter = useMemo(
    () => LOCATION_FIELDS.some((field) => field.show(showFilters)),
    [showFilters]
  );

  const dateFilterEntries = useMemo(() => Object.entries(DATE_FILTER_CONFIG), []);
  const filterEntries = useMemo(() => Object.entries(FILTER_CONFIG), []);
  const specialFilterEntries = useMemo(() => Object.entries(SPECIAL_SELECT_CONFIG), []);
  const textFilterEntries = useMemo(() => Object.entries(TEXT_FILTER_CONFIG), []);

  const getVisibleFilterEntries = useCallback(
    (entries: [string, any][]) => entries.filter(([key]) => showFilters?.[key as FilterKey]?.show),
    [showFilters]
  );

  useEffect(() => {
    if (!open) {
      setPaginatedSelectResetKey((k) => k + 1);
      return;
    }

    setFilters({
      ...INITIAL_FILTERS,
      ...appliedFilter,
      dateType: appliedFilter?.dateType || defaultDateTypeOption,
      fromDate: appliedFilter?.fromDate ? dayjs(appliedFilter.fromDate) : null,
      toDate: appliedFilter?.toDate ? dayjs(appliedFilter.toDate) : null,
      fromYear: appliedFilter?.fromYear ? dayjs(appliedFilter.fromYear) : null,
      toYear: appliedFilter?.toYear ? dayjs(appliedFilter.toYear) : null,
      location: appliedFilter?.location || EMPTY_LOCATION,
    });

    setDateError("");
    setYearError("");
    setExpandedSections({ date: true, year: true, location: true });
  }, [open, appliedFilter, defaultDateTypeOption]);

  useEffect(() => {
    const countryId = filters.location?.country?.CountryId;
    if (countryId) dispatch(getStateList(countryId));
  }, [filters.location?.country?.CountryId, dispatch]);

  useEffect(() => {
    const stateId = filters.location?.state?.StateId;
    const shouldLoadCity = showFilters?.location?.city?.show;

    if (stateId && shouldLoadCity) dispatch(getCityList(stateId));
  }, [filters.location?.state?.StateId, showFilters?.location?.city?.show, dispatch]);

  useEffect(() => {
    const cityId = filters.location?.city?.CityId;
    const shouldLoadLocality = showFilters?.location?.locality?.show;

    if (cityId && shouldLoadLocality) {
      dispatch(getLocalityList(cityId));
    }
  }, [filters.location?.city?.CityId, showFilters?.location?.locality?.show, dispatch]);

  useEffect(() => {
    const groupId = filters?.groupId?.Id;
    if (groupId && showFilters?.type?.show) dispatch(getTypeNames({ groupId: groupId }));
  }, [filters?.groupId?.Id, showFilters?.type?.show, dispatch]);

  useEffect(() => {
    const typeId = filters?.typeId?.TypeId;
    if (typeId && (showFilters?.item?.show)) dispatch(getAssetItemNames({ typeId: typeId }));
  }, [filters?.typeId?.TypeId, showFilters?.item?.show, dispatch]);

  const comparableFilters = useMemo(
    () => normalizeValue(buildComparableFilter(filters)),
    [filters]
  );

  const comparableAppliedFilters = useMemo(
    () => normalizeValue(buildComparableFilter(appliedFilter)),
    [appliedFilter]
  );

  const isFilterChanged = useMemo(() => {
    return JSON.stringify(comparableFilters) !== JSON.stringify(comparableAppliedFilters);
  }, [comparableFilters, comparableAppliedFilters]);

  // clear (apply empty + close) — existing behavior
  const handleClear = () => {
    const cleared = {
      ...INITIAL_FILTERS,
      dateType: defaultDateTypeOption,
    };

    setFilters(cleared);
    onApply(cleared);
    onClose();
  };

  // Apply
  const handleApply = () => {
    if (filters.fromDate && !filters.toDate) {
      setDateError("Please select To Date");
      return;
    }

    if (filters.fromDate && filters.toDate && filters.toDate.isBefore(filters.fromDate, "day")) {
      return setDateError("To Date cannot be earlier than From Date");
    }

    if (showFilters?.year?.mode !== "single") {
      if (filters.fromYear && !filters.toYear) {
        setYearError("Please select To Year");
        return;
      }

      if (filters.fromYear && filters.toYear && filters.toYear.isBefore(filters.fromYear, "year")) {
        setYearError("To Year cannot be earlier than From Year");
        return;
      }
    }

    if (!isFilterChanged) return onClose();

    onApply({
      ...filters,
      dateType: filters.dateType || "",
    });

    onClose();
  };

  const dateTypeValue = useMemo(() => {
    return typeof filters.dateType === "object" ? filters.dateType?.value : null;
  }, [filters.dateType]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        },
      }}
    >
      <div style={{ width: width || "400px", maxWidth: "95vw" }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-solid border-[#e9ecef]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="flex items-center justify-center rounded-md shrink-0"
              style={{ width: 32, height: 32, backgroundColor: "#eef0fb" }}
            >
              <FilterListIcon sx={{ fontSize: 18, color: PRIMARY_BLUE }} />
            </div>
            <span className="font-semibold text-[16px] text-[#212529]">Filters</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <IconButton onClick={onClose} size="small" sx={{ color: "#74788d" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className="px-4 py-2 max-h-[70vh] overflow-y-auto">
          {/* Date Section */}
          {showFilters?.date?.show && (
            <div className="py-3 border-b border-solid border-[#f0f2f5]">
              <FilterSectionHeader
                title={defaultDateLabel || "By Date"}
                icon={CalendarMonthIcon}
                expanded={expandedSections.date}
                onToggle={() => toggleSection("date")}
              />
              {expandedSections.date && (
                <>
                  <div className={`grid gap-4 ${(!showFilters?.dateType?.show) ? "grid-cols-2" : "grid-cols-3"}`}>
                    {showFilters?.dateType?.show && (
                      <div>
                        <FieldLabel>Date Type</FieldLabel>
                        <Select
                          field={{
                            value: filters.dateType,
                            onChange: (e: any) => setFilters((p: any) => ({ ...p, dateType: e }))
                          }}
                          options={dateTypeOptions || []}
                          idKey="value"
                          nameKey="label"
                          returnObject
                          enableSearch={false}
                          variant="outlined"
                          size="small"
                          hideLabel
                          placeholder="Select date type"
                          sx={BOX_INPUT_SX}
                        />
                      </div>
                    )}

                    <div>
                      <FieldLabel>From Date</FieldLabel>
                      <DateInput
                        field={{
                          value: filters.fromDate,
                          onChange: (v: any) => {
                            setDateError("");
                            setFilters((p: any) => ({ ...p, fromDate: v }));
                          }
                        }}
                        format="DD MMM, YYYY"
                        {...(!isLeaveModule && {
                          maxDate: dayjs()
                        })}
                        slotProps={boxDateSlotProps({ placeholder: "Select date" })}
                      />
                    </div>

                    <div>
                      <FieldLabel>To Date</FieldLabel>
                      <DateInput
                        field={{
                          value: filters.toDate,
                          onChange: (v: any) => {
                            setDateError("");
                            setFilters((p: any) => ({ ...p, toDate: v }));
                          }
                        }}
                        minDate={dayjs(filters.fromDate) || undefined}
                        format="DD MMM, YYYY"
                        {...(!isLeaveModule && {
                          disableFuture: dateTypeValue !== "CloseDate",
                          ...(filterName !== "order" &&
                            filterName !== "visitForm" &&
                            dateTypeValue !== "CloseDate" && {
                            maxDate: dayjs(),
                          }),
                        })}
                        slotProps={boxDateSlotProps({
                          placeholder: "Select date",
                          error: !!dateError,
                          helperText: dateError,
                        })}
                      />
                    </div>
                  </div>

                  {showFilters?.stage?.show && filterName === "customerView" && (
                    <div className="mt-4">
                      <FieldLabel>Stage</FieldLabel>
                      <Select
                        field={{
                          value: filters?.stageIds?.[0] || null,
                          onChange: (v: any) =>
                            setFilters((p: any) => ({
                              ...p,
                              stageIds: v ? [v] : [],
                            }))
                        }}
                        options={list?.stageList || []}
                        idKey="Id"
                        nameKey="Name"
                        returnObject
                        variant="outlined"
                        size="small"
                        hideLabel
                        placeholder="Select stage"
                        sx={BOX_INPUT_SX}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Year Section */}
          {showFilters?.year?.show && (
            <div className="py-3 border-b border-solid border-[#f0f2f5]">
              <FilterSectionHeader
                title={`${showFilters.qualification?.show && showFilters?.year?.mode !== "single" ? "By Qualification" : "By Year"}`}
                icon={SchoolIcon}
                expanded={expandedSections.year}
                onToggle={() => toggleSection("year")}
              />
              {expandedSections.year && (
                <div className={`grid ${showFilters.qualification?.show ? "grid-cols-3" : "grid-cols-2"} gap-4`}>
                  {/* Qualification Filter */}
                  {showFilters?.qualification?.show && (
                    <Select
                      label="Qualification"
                      field={{
                        value: filters?.qualification,
                        onChange: (v: any) => setFilters((p: any) => ({ ...p, qualification: v }))
                      }}
                      options={list?.qualificationList || []}
                      idKey="Qualification"
                      nameKey="Qualification"
                      returnObject
                      variant="outlined"
                      size="small"
                      hideLabel
                      placeholder="Select qualification"
                      sx={BOX_INPUT_SX}
                    />
                  )}

                  {/* From Year */}
                  <DateInput
                    views={["year"]}
                    label={showFilters?.year?.mode !== "single" ? "From Year" : "Year"}
                    field={{
                      value: filters.fromYear,
                      onChange: (v: any) => {
                        setYearError("");
                        setFilters((p: any) => ({ ...p, fromYear: v }));
                      }
                    }}
                    format="YYYY"
                    {...(showFilters?.year?.mode !== "single" && { maxDate: dayjs() })}
                    slotProps={{
                      textField: { variant: "standard", fullWidth: true, placeholder: showFilters?.year?.mode !== "single" ? "From Year" : "Year" }
                    }}
                  />

                  {/* To Year */}
                  {showFilters?.year?.mode !== "single" && (
                    <DateInput
                      views={["year"]}
                      label="To Year"
                      field={{
                        value: filters.toYear,
                        onChange: (v: any) => {
                          setYearError("");
                          setFilters((p: any) => ({ ...p, toYear: v }));
                        }
                      }}
                      {...(filters.fromYear && { minDate: filters.fromYear })}
                      format="YYYY"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          fullWidth: true,
                          placeholder: "To Year",
                          error: !!yearError,
                          helperText: yearError,
                        },
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* location filter */}
          {hasVisibleLocationFilter && (
            <div className="py-3 border-b border-solid border-[#f0f2f5]">
              <FilterSectionHeader
                title="By Location"
                icon={LocationOnIcon}
                expanded={expandedSections.location}
                onToggle={() => toggleSection("location")}
              />
              {expandedSections.location && (
                <Box
                  display="grid"
                  gridTemplateColumns={getLocationGridTemplateColumns(filterName)}
                  gap={2}
                  sx={{
                    "& > *": {
                      minWidth: 0,
                    },
                  }}
                >
                  {LOCATION_FIELDS
                    .filter((field) => field.show(showFilters))
                    .map((field) => (
                      <GenericSelectFilter
                        key={field.key}
                        locationClassName="px-0"
                        hideTitle
                        config={{
                          label: field.label,
                          filterKey: field.key,
                          idKey: field.idKey,
                          nameKey: field.nameKey,
                          disabled: () => field.dependsOn ? !filters?.location?.[field.dependsOn] : false,
                        }}
                        filters={filters.location}
                        options={locationOptionsMap[field.optionSource as keyof typeof locationOptionsMap]}
                        customOnChange={(value: any) => {
                          setFilters((prev: any) => {
                            const location = {
                              ...prev.location,
                              [field.key]: value,
                            };

                            field.reset?.forEach((k) => {
                              location[k] = null;
                            });

                            return {
                              ...prev,
                              location,
                            };
                          });
                        }}
                      />
                    ))}
                </Box>
              )}
            </div>
          )}

          <div className={getFilterGridClassName(filterName)}>
            {getVisibleFilterEntries(dateFilterEntries).map(([key, config]) => (
              <GenericDateFilter
                key={key}
                {...config}
                filterKey={key}
                filters={filters}
                updateFilter={updateFilter}
              />
            ))}

            {getVisibleFilterEntries(filterEntries).map(([key, config]) => {
              const visibility = showFilters?.[key as FilterKey];
              const optionKey = config.optionKey as keyof NonNullable<list>;
              const data = list?.[optionKey];

              const options = Array.isArray(data)
                ? config.transformOptions
                  ? data.map((item: string, index: number) => ({
                    Id: index,
                    Name: item,
                  }))
                  : data
                : [];

              return (
                <GenericSelectFilter
                  key={key}
                  config={config}
                  filters={filters}
                  updateFilter={updateFilter}
                  options={options}
                  filterName={filterName}
                  showFilter={visibility}
                  {...(config.usePaginatedSelect && {
                    onFetchCustomerPage,
                    paginatedSelectResetKey,
                  })}
                />
              );
            })}

            {getVisibleFilterEntries(specialFilterEntries).map(([key, config]) => (
              <GenericSelectFilter
                key={key}
                config={config}
                filters={filters}
                updateFilter={updateFilter}
                showFilter={showFilters?.[key as FilterKey]}
                options={
                  specialOptionsMap[
                  config.optionSource as keyof typeof specialOptionsMap
                  ] || []
                }
              />
            ))}
          </div>

          <div className={getTextGridTemplateColumns(filterName)}>
            {getVisibleFilterEntries(textFilterEntries).map(([key, config]) => (
              <GenericTextFilter
                key={key}
                {...config}
                filters={filters}
                updateFilter={updateFilter}
              />
            ))}
          </div>

          {/* Be GeoFenceType */}
          {showFilters?.geoFenceType?.show && (
            <div className="py-3 border-b border-solid border-[#f0f2f5]">
              <FilterSectionHeader title="By Geofence Type" icon={LocationOnIcon} />
              <div className="grid gap-4 grid-cols-2">
                {GEOFENCE_FILTER_CONFIG
                  .filter((config) => config.type === "geoFenceType")
                  .map((config) => (
                    <GenericSelectFilter
                      key={config.filterKey}
                      config={config}
                      filters={filters}
                      updateFilter={updateFilter}
                      options={list?.geoFenceList || []}
                      showFilter={showFilters?.geoFenceType}
                      hideLabel
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Be GeoFenceName */}
          {showFilters?.geoFenceName?.show && (
            <div className="py-3">
              <FilterSectionHeader title="By Geofence Name" icon={LocationOnIcon} />
              <div className="grid gap-4 grid-cols-2">
                {GEOFENCE_FILTER_CONFIG
                  .filter((config) => config.type === "geoFenceName")
                  .map((config) => (
                    <GenericSelectFilter
                      key={config.filterKey}
                      config={config}
                      filters={filters}
                      updateFilter={updateFilter}
                      options={list?.geoFenceNameList || []}
                      showFilter={showFilters?.geoFenceName}
                      hideLabel
                    />
                  ))}
              </div>
            </div>
          )}

          {/* By Configuration Status */}
          {showFilters?.settingType?.show && (
            <div className="py-3">
              <FilterSectionHeader title="By Configuration Status" icon={LocationOnIcon} />
              <div className="grid gap-4 grid-cols-2">
                <GenericSelectFilter
                  config={{
                    label: "Type",
                    filterKey: "settingType",
                    idKey: "value",
                    nameKey: "label",
                  }}
                  filters={filters}
                  updateFilter={updateFilter}
                  customOnChange={(v: any) => {
                    updateFilter("settingType", v);
                    if (v) {
                      if (!filters?.settingStatus) {
                        updateFilter("settingStatus", { label: "Enable", value: true });
                      }
                    } else {
                      updateFilter("settingStatus", null);
                    }
                  }}
                  options={list?.settingTypeList || []}
                  showFilter={showFilters?.settingType}
                  hideLabel
                />
                <GenericSelectFilter
                  config={{
                    label: "Type Status",
                    filterKey: "settingStatus",
                    idKey: "value",
                    nameKey: "label",
                    disabled: (f: any) => !f?.settingType,
                  }}
                  filters={filters}
                  updateFilter={updateFilter}
                  options={list?.settingStatusList || [
                    { label: "Enable", value: true },
                    { label: "Disable", value: false },
                  ]}
                  showFilter={showFilters?.settingType}
                  hideLabel
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 py-3 border-t border-solid border-[#e9ecef]">
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              textTransform: "none",
              color: "#495057",
              borderColor: "#ced4da",
              backgroundColor: "#fff",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#adb5bd",
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            startIcon={<FilterAltIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: PRIMARY_BLUE,
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4a58c7",
                boxShadow: "none",
              },
            }}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default memo(FilterComponent);
